import { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import AcquireProfileModal from "./AcquireProfileModal";
import PayProfileModal from "./PayProfileModal";
import ChangePlanPromo from "./ChangePlanPromo";

export default function RenewMembership({ isOpen, onClose }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [selectedProfiles, setSelectedProfiles] = useState(null);
  const [currentProfileCount, setCurrentProfileCount] = useState(0);
  const [isMembershipExpired, setIsMembershipExpired] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [readyToRenderPayPal, setReadyToRenderPayPal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [showProfilePaymentModal, setShowProfilePaymentModal] = useState(false);
  const [showAcquireProfileModal, setShowAcquireProfileModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [promoProfiles, setPromoProfiles] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const exchangeRate = Number(process.env.REACT_APP_EXCHANGE_RATE) || 60;

  const plans = [
    {
      id: "basic",
      title: "Plan Básico",
      postsPerMonth: 5,
      priceDOP: 500,
    },
    {
      id: "premium",
      title: "Plan Premium",
      postsPerMonth: 10,
      priceDOP: 1000,
    },
  ];

  const monthsOptions = [1, 3, 6, 12];

  const profilesPricing = [
    { count: 1, priceDOP: 500 },
    { count: 2, priceDOP: 1000 },
    { count: 3, priceDOP: 1500 },
  ];

  const getPlanPrice = (plan, months, profiles) => ({
    usd: Number(
      (
        (plan.priceDOP * months +
          (profilesPricing.find((p) => p.count === profiles)?.priceDOP || 0)) /
        exchangeRate
      ).toFixed(2)
    ),
    dop: plan.priceDOP * months + (profilesPricing.find((p) => p.count === profiles)?.priceDOP || 0),
  });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          const profilesQuery = query(
            collection(db, "promoProfiles"),
            where("userId", "==", user.uid),
            where("status", "==", "COMPLETED")
          );
          const profilesSnapshot = await getDocs(profilesQuery);
          let profileCount = 0;
          let isExpired = true;
          const now = new Date();

          const promoProfilesData = profilesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          profileCount = promoProfilesData.reduce((sum, profile) => sum + (profile.profileCount || 0), 0);
          isExpired = !promoProfilesData.some((profile) => profile.expirationDate?.toDate() > now);

          const userPromoProfilesQuery = query(collection(db, `users/${user.uid}/promoProfiles`));
          const userPromoProfilesSnapshot = await getDocs(userPromoProfilesQuery);
          const userPromoProfilesData = userPromoProfilesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const combinedProfiles = userPromoProfilesData.map((userProfile) => {
            const matchingProfile = promoProfilesData.find((p) => p.userId === user.uid);
            return {
              id: userProfile.id,
              name: userProfile.name,
              description: userProfile.description,
              profilePhotoUrl: userProfile.profilePhotoUrl,
              tags: userProfile.tags,
              planType: matchingProfile?.planType,
              postsPerMonth: matchingProfile?.postsPerMonth,
              expirationDate: matchingProfile?.expirationDate,
            };
          });

          setCurrentProfileCount(profileCount);
          setIsMembershipExpired(isExpired);
          setPromoProfiles(combinedProfiles);
        } catch (error) {
          console.error("Error verificando perfiles:", error);
          setPaypalError("Error al verificar tu membresía.");
        }
      } else {
        setUserId(null);
        setPaypalError("Debes iniciar sesión para renovar.");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handlePaymentClick = async () => {
    if (!selectedPlan) {
      setPaypalError("Selecciona un plan válido.");
      return;
    }
    if (!selectedProfiles || selectedProfiles + currentProfileCount > 3) {
      setPaypalError(`Solo puedes tener hasta 3 perfiles. Actualmente tienes ${currentProfileCount}.`);
      return;
    }
    if (!userId) {
      setPaypalError("Debes iniciar sesión para continuar.");
      return;
    }
    if (loading) {
      setPaypalError("Cargando datos, por favor espera...");
      return;
    }
    if (readyToRenderPayPal) {
      return; // Evita múltiples órdenes mientras una está activa
    }

    setProcessing(true);
    setPaypalError(null);

    try {
      const price = getPlanPrice(selectedPlan, selectedMonths, selectedProfiles).usd;
      const response = await fetch(`${backendUrl}/api/renewals/create-renewal-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: price,
          months: selectedMonths,
          userId,
          planType: selectedPlan.id,
          profileCount: selectedProfiles,
        }),
      });
      const data = await response.json();
      if (data.orderId) {
        setOrderId(data.orderId);
        setReadyToRenderPayPal(true);
      } else {
        setPaypalError(data.error || "Error al crear la orden de pago.");
      }
    } catch (error) {
      setPaypalError("Error al conectar con el servidor.");
    } finally {
      setProcessing(false);
    }
  };

  const handleSuccess = () => {
    setPaymentSuccess(true);
    setTimeout(() => {
      setPaymentSuccess(false);
      setReadyToRenderPayPal(false);
      setOrderId(null);
      setSelectedPlan(null);
      setSelectedMonths(1);
      setSelectedProfiles(null);
      setShowAcquireProfileModal(false);
      onClose();
    }, 2000);
  };

  const handleProfilePaymentSuccess = () => {
    setPaymentSuccess(true);
    setTimeout(() => {
      setPaymentSuccess(false);
      setReadyToRenderPayPal(false);
      setOrderId(null);
      setShowProfilePaymentModal(false);
    }, 2000);
  };

  const handleError = (err) => {
    setPaypalError(err?.message || "Error con el pago.");
    setProcessing(false);
    setReadyToRenderPayPal(false);
    setOrderId(null); // Asegúrate de limpiar orderId en caso de error
  };

  const handleOpenProfilePaymentModal = () => {
    if (readyToRenderPayPal) {
      return; // Evita abrir el modal si ya hay un pago en curso
    }
    setShowProfilePaymentModal(true);
    setReadyToRenderPayPal(false); // Asegúrate de que no haya un botón de PayPal activo al abrir
    setOrderId(null); // Limpia cualquier orden previa
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={() => !processing && onClose()}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80" />
          </Transition.Child>

          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="ease-in duration-200"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Dialog.Panel className="w-full max-w-lg sm:max-w-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl transition-all">
                <button
                  onClick={() => !processing && onClose()}
                  className="absolute top-4 right-4 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-2xl font-bold transition-transform hover:scale-125"
                >
                  ×
                </button>

                {paymentSuccess && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-400 to-green-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-bounce z-50">
                    <span className="text-2xl">🎉</span>
                    <span className="font-bold text-lg">¡Pago completado con éxito!</span>
                  </div>
                )}

                <Dialog.Title className="text-3xl sm:text-4xl font-extrabold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-300 dark:to-purple-400">
                  Renovar Membresía
                </Dialog.Title>

                <button
                  onClick={handleOpenProfilePaymentModal}
                  className="w-full mb-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50"
                  disabled={processing || readyToRenderPayPal}
                >
                  Pagar Mis Perfiles Promocionales
                </button>

                <button
                  onClick={() => setShowAcquireProfileModal(true)}
                  className="w-full mb-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50"
                  disabled={processing || readyToRenderPayPal}
                >
                  Adquirir Otro Perfil Promocional
                </button>

                <button
                  onClick={() => setShowChangePlanModal(true)}
                  className="w-full mb-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50"
                  disabled={processing || readyToRenderPayPal}
                >
                  Cambiar tipo de plan para mi perfil
                </button>

                {loading ? (
                  <div className="text-center text-gray-600 dark:text-gray-300">Cargando...</div>
                ) : isMembershipExpired ? (
                  <div className="text-center text-gray-600 dark:text-gray-300">
                    Tu membresía ha expirado. Por favor, selecciona un plan para renovar.
                  </div>
                ) : (
                  <div className="text-center text-gray-600 dark:text-gray-300">
                    Tu membresía está activa. Usa las opciones arriba para gestionar tus perfiles.
                  </div>
                )}

                {paypalError && (
                  <p className="text-center text-red-500 font-semibold text-lg mt-6 bg-red-100/50 dark:bg-red-900/50 py-2 rounded-lg">
                    {paypalError}
                  </p>
                )}

                <button
                  onClick={() => !processing && onClose()}
                  className="w-full mt-8 text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors text-base font-medium"
                >
                  Cancelar
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      <AcquireProfileModal
        isOpen={showAcquireProfileModal}
        onClose={() => setShowAcquireProfileModal(false)}
        plans={plans}
        monthsOptions={monthsOptions}
        profilesPricing={profilesPricing}
        currentProfileCount={currentProfileCount}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        selectedMonths={selectedMonths}
        setSelectedMonths={setSelectedMonths}
        selectedProfiles={selectedProfiles}
        setSelectedProfiles={setSelectedProfiles}
        getPlanPrice={getPlanPrice}
        readyToRenderPayPal={readyToRenderPayPal}
        setReadyToRenderPayPal={setReadyToRenderPayPal}
        userId={userId}
        clientId={clientId}
        paypalError={paypalError}
        setPaypalError={setPaypalError}
        processing={processing}
        setProcessing={setProcessing}
        onSuccess={handleSuccess}
        onError={handleError}
      />

      <PayProfileModal
        isOpen={showProfilePaymentModal}
        onClose={() => {
          setShowProfilePaymentModal(false);
          setReadyToRenderPayPal(false);
          setOrderId(null);
        }}
        promoProfiles={promoProfiles}
        userId={userId}
        clientId={clientId}
        exchangeRate={exchangeRate}
        readyToRenderPayPal={readyToRenderPayPal}
        setReadyToRenderPayPal={setReadyToRenderPayPal}
        orderId={orderId}
        setOrderId={setOrderId}
        paypalError={paypalError}
        setPaypalError={setPaypalError}
        processing={processing}
        setProcessing={setProcessing}
        onSuccess={handleProfilePaymentSuccess}
        onError={handleError}
      />

      <ChangePlanPromo
        isOpen={showChangePlanModal}
        onClose={() => {
          setShowChangePlanModal(false);
          setReadyToRenderPayPal(false);
          setOrderId(null);
        }}
        promoProfiles={promoProfiles}
        userId={userId}
        clientId={clientId}
        exchangeRate={exchangeRate}
        backendUrl={backendUrl}
        plans={plans}
      />
    </>
  );
}